import { computed } from 'vue';
import { defineAnnotationToolStore } from '@/src/utils/defineAnnotationToolStore';
import type { Vector3 } from '@kitware/vtk.js/types';
import {
  angleBetweenVectors,
  subtract,
  degreesFromRadians,
} from '@kitware/vtk.js/Common/Core/Math';
import { ToolID } from '@/src/types/annotation-tool';

import { ANGLE_LABEL_DEFAULTS } from '@/src/config';
import { Manifest, StateFile } from '@/src/io/state-file/schema';

import { useAnnotationTool } from './useAnnotationTool';

const angleDefaults = () => ({
  firstPoint: [0, 0, 0] as Vector3,
  secondPoint: [0, 0, 0] as Vector3,
  thirdPoint: [0, 0, 0] as Vector3,
  id: '' as ToolID,
  name: 'Angle',
});

export const useAngleStore = defineAnnotationToolStore('angle', () => {
  const toolAPI = useAnnotationTool({
    toolDefaults: angleDefaults,
    initialLabels: ANGLE_LABEL_DEFAULTS,
  });

  // Angle (degrees) at the second (vertex) point.
  const angleByID = computed<Record<string, number>>(() => {
    const byID = toolAPI.toolByID.value;
    return toolAPI.toolIDs.value.reduce((angles, id) => {
      const { firstPoint, secondPoint, thirdPoint } = byID[id];
      const v1 = [0, 0, 0] as Vector3;
      const v2 = [0, 0, 0] as Vector3;
      subtract(firstPoint, secondPoint, v1);
      subtract(thirdPoint, secondPoint, v2);
      const radians = angleBetweenVectors(v1, v2);
      return Object.assign(angles, {
        [id]: degreesFromRadians(radians),
      });
    }, {});
  });

  function getPoints(id: ToolID) {
    const tool = toolAPI.toolByID.value[id];
    return [tool.firstPoint, tool.secondPoint, tool.thirdPoint];
  }

  // --- serialization --- //

  function serialize(state: StateFile) {
    if (!state.manifest.tools) return;
    state.manifest.tools.angles = toolAPI.serializeTools();
  }

  function deserialize(manifest: Manifest, dataIDMap: Record<string, string>) {
    toolAPI.deserializeTools(manifest.tools?.angles, dataIDMap);
  }

  return {
    ...toolAPI,
    angleByID,
    getPoints,
    serialize,
    deserialize,
  };
});
